import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Org } from '@weave/schema-gen-ts/dist/apis/api/identity/v1/api.pb';
import { isEqual } from 'lodash-es';
import { getUser, setLoginData } from '@frontend/auth-helpers';
import { shell, useShellLocationsSyncStore } from '@frontend/shell-utils';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { useAllAccessScopeStore } from './all-access-store';
import { WeaveLocation, WeaveLocationGroup } from './types';

export type OrgIdMap = Record<string, OrgIdMapKey>;

export type OrgIdMapKey = { parents: WeaveLocationGroup[]; locations: WeaveLocation[] };
interface ScopeStore {
  locationData: Record<string, WeaveLocation>;
  setLocationData: (scopes: Record<string, WeaveLocation>) => void;
  groupData: Record<string, WeaveLocationGroup>;
  setGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  orgData: Record<string, Org>;
  setOrgData: (orgs: Record<string, Org>) => void;
  selectedOrgId: string;
  setSelectedOrgId: (orgId: string) => void;
  selectedGroupId: string;
  setSelectedGroupId: (groupId: string) => void;
  forcedGroupId: string;
  setForcedGroupId: (groupId: string) => void;
  selectedLocationIds: string[];
  setSelectedLocationIds: (scopes: string[]) => void;
  appendLocationData: (scopes: Record<string, WeaveLocation>) => void;
  appendGroupData: (scopes: Record<string, WeaveLocationGroup>) => void;
  appendOrgData: (orgs: Record<string, Org>) => void;
  scopeHierarchy: Record<string, Record<string, string[]>>;
  setScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;
  appendScopeHierarchy: (hierarchy: Record<string, Record<string, string[]>>) => void;

  // Derived/computed properties — updated atomically by setters above
  accessibleLocationMap: Record<string, WeaveLocation>;
  accessibleGroupMap: Record<string, WeaveLocationGroup>;
  accessibleScopeHierarchy: Record<string, Record<string, string[]>>;
  accessibleGroupIds: string[];
  accessibleLocationIds: string[];
  accessibleOrgIds: string[];
  isSingleTypeScope: boolean;
}

const computeAccessibleLocationMap = (locationData: Record<string, WeaveLocation>): Record<string, WeaveLocation> =>
  Object.entries(locationData).reduce((acc, [id, location]) => {
    if (location.accessible && location.active) acc[id] = { ...location };
    return acc;
  }, {} as Record<string, WeaveLocation>);

const computeAccessibleGroupMap = (groupData: Record<string, WeaveLocationGroup>): Record<string, WeaveLocationGroup> =>
  Object.entries(groupData).reduce((acc, [id, group]) => {
    if (group.accessible && group.active) {
      acc[id] = {
        ...group,
        children: group.children.filter((l) => l.accessible && l.active),
      };
    }
    return acc;
  }, {} as Record<string, WeaveLocationGroup>);

const computeAccessibleScopeHierarchy = (
  scopeHierarchy: Record<string, Record<string, string[]>>,
  accessibleLocationMap: Record<string, WeaveLocation>,
  accessibleGroupMap: Record<string, WeaveLocationGroup>
): Record<string, Record<string, string[]>> =>
  Object.entries(scopeHierarchy).reduce((orgAcc, [orgId, org]) => {
    orgAcc[orgId] = Object.entries(org).reduce((groupAcc, [groupId, locationIds]) => {
      const isGroupAccessible = !!accessibleGroupMap[groupId];
      const accessibleLocationIdsInGroup = locationIds.filter((id) => !!accessibleLocationMap[id]);
      if (isGroupAccessible || accessibleLocationIdsInGroup.length > 0) {
        groupAcc[groupId] = accessibleLocationIdsInGroup;
      }
      return groupAcc;
    }, {} as Record<string, string[]>);
    return orgAcc;
  }, {} as Record<string, Record<string, string[]>>);

const computeIsSingleTypeScope = (
  selectedLocationIds: string[],
  groupData: Record<string, WeaveLocationGroup>,
  forcedGroupId: string
): boolean => selectedLocationIds.length === 1 && groupData[forcedGroupId]?.locationType === 'single';

export const createScopeStore = () =>
  createStoreWithSubscribe<ScopeStore>(
    (set, get) => ({
      locationData: {},
      setLocationData: (locations) => {
        const accessibleLocationMap = computeAccessibleLocationMap(locations);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          accessibleLocationMap,
          get().accessibleGroupMap
        );
        set(
          {
            locationData: locations,
            accessibleLocationMap,
            accessibleLocationIds: Object.keys(accessibleLocationMap),
            accessibleScopeHierarchy,
          },
          false,
          { type: 'setLocationData' }
        );
        useAllAccessScopeStore.getState().setAccessibleLocationMap(accessibleLocationMap);
      },
      groupData: {},
      setGroupData: (groups) => {
        const accessibleGroupMap = computeAccessibleGroupMap(groups);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          get().accessibleLocationMap,
          accessibleGroupMap
        );
        const isSingleTypeScope = computeIsSingleTypeScope(get().selectedLocationIds, groups, get().forcedGroupId);
        set(
          {
            groupData: groups,
            accessibleGroupMap,
            accessibleGroupIds: Object.keys(accessibleGroupMap),
            accessibleScopeHierarchy,
            isSingleTypeScope,
          },
          false,
          { type: 'setGroupData' }
        );
        useAllAccessScopeStore.getState().setAccessibleGroupMap(accessibleGroupMap);
      },
      orgData: {},
      setOrgData: (orgs) => {
        set({ orgData: orgs, accessibleOrgIds: Object.keys(orgs) }, false, { type: 'setOrgData' });
      },
      selectedOrgId: '',
      setSelectedOrgId: (orgId) => {
        set({ selectedOrgId: orgId }, false, { type: 'setSelectedOrgId' });
      },
      selectedGroupId: '',
      setSelectedGroupId: (groupId) => {
        set({ selectedGroupId: groupId }, false, { type: 'setSelectedGroupId' });
      },
      forcedGroupId: '',
      setForcedGroupId: (groupId) => {
        const isSingleTypeScope = computeIsSingleTypeScope(get().selectedLocationIds, get().groupData, groupId);
        set({ forcedGroupId: groupId, isSingleTypeScope }, false, { type: 'setForcedGroupId' });
      },
      selectedLocationIds: [],
      setSelectedLocationIds: (scopes) => {
        const isSingleTypeScope = computeIsSingleTypeScope(scopes, get().groupData, get().forcedGroupId);
        set({ selectedLocationIds: scopes, isSingleTypeScope }, false, { type: 'setSelectedLocationIds' });
      },
      appendLocationData: (locations) => {
        const newLocationData = { ...get().locationData, ...locations };
        const accessibleLocationMap = computeAccessibleLocationMap(newLocationData);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          accessibleLocationMap,
          get().accessibleGroupMap
        );
        set(
          {
            locationData: newLocationData,
            accessibleLocationMap,
            accessibleLocationIds: Object.keys(accessibleLocationMap),
            accessibleScopeHierarchy,
          },
          false,
          { type: 'appendLocationData' }
        );
        useAllAccessScopeStore.getState().setAccessibleLocationMap(accessibleLocationMap);
      },
      appendGroupData: (groups) => {
        const newGroupData = { ...get().groupData, ...groups };
        const accessibleGroupMap = computeAccessibleGroupMap(newGroupData);
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          get().scopeHierarchy,
          get().accessibleLocationMap,
          accessibleGroupMap
        );
        const isSingleTypeScope = computeIsSingleTypeScope(
          get().selectedLocationIds,
          newGroupData,
          get().forcedGroupId
        );
        set(
          {
            groupData: newGroupData,
            accessibleGroupMap,
            accessibleGroupIds: Object.keys(accessibleGroupMap),
            accessibleScopeHierarchy,
            isSingleTypeScope,
          },
          false,
          { type: 'appendGroupData' }
        );
        useAllAccessScopeStore.getState().setAccessibleGroupMap(accessibleGroupMap);
      },
      appendOrgData: (orgs) => {
        const newOrgData = { ...get().orgData, ...orgs };
        set({ orgData: newOrgData, accessibleOrgIds: Object.keys(newOrgData) }, false, { type: 'appendOrgData' });
      },
      scopeHierarchy: {},
      setScopeHierarchy: (hierarchy) => {
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          hierarchy,
          get().accessibleLocationMap,
          get().accessibleGroupMap
        );
        set({ scopeHierarchy: hierarchy, accessibleScopeHierarchy }, false, { type: 'setScopeHierarchy' });
      },
      appendScopeHierarchy: (hierarchy) => {
        const newScopeHierarchy = { ...get().scopeHierarchy, ...hierarchy };
        const accessibleScopeHierarchy = computeAccessibleScopeHierarchy(
          newScopeHierarchy,
          get().accessibleLocationMap,
          get().accessibleGroupMap
        );
        set({ scopeHierarchy: newScopeHierarchy, accessibleScopeHierarchy }, false, {
          type: 'appendScopeHierarchy',
        });
      },

      // Derived/computed properties — initialized with defaults, updated atomically by setters above
      accessibleLocationMap: {},
      accessibleGroupMap: {},
      accessibleScopeHierarchy: {},
      accessibleGroupIds: [],
      accessibleLocationIds: [],
      accessibleOrgIds: [],
      isSingleTypeScope: false,
    }),
    { name: 'ScopeStore', trace: true }
  );

export const useLocationGroupStore = createScopeStore();
const useScopeShallowStore = createShallowStore(useLocationGroupStore);

/**
 * Sends selected location ids to shell when they change.
 * lastSentRef must be a useRef so each component instance tracks its own "last sent" state
 * independently — avoiding the stale-closure/shared-state bug of a module-level variable.
 */
const syncLocationsToShell = ({
  selectedLocationIds,
  lastSentRef,
}: {
  selectedLocationIds: string[];
  lastSentRef: { current: string[] };
}) => {
  if (shell.isShell && !isEqual(lastSentRef.current, selectedLocationIds) && selectedLocationIds.length !== 0) {
    lastSentRef.current = selectedLocationIds ?? [];

    if (shell.featureAvailability?.has('zustand-sync')) {
      useShellLocationsSyncStore.getState().setSelectedLocationIds(selectedLocationIds ?? []);
    } else {
      shell.emit?.('send:locations', { type: 'locations', locations: selectedLocationIds ?? [] });
    }
  }
};

/**
 * Pure utility — returns a scope name lookup function given the relevant data maps.
 * Shared between useAppScopeStore and useAppScopeStoreWithSetters to avoid duplication.
 */
const computeGetScopeName =
  (
    locationData: Record<string, WeaveLocation>,
    groupData: Record<string, WeaveLocationGroup>,
    orgData: Record<string, Org>,
    accessibleLocationMap: Record<string, WeaveLocation>,
    accessibleGroupMap: Record<string, WeaveLocationGroup>
  ) =>
  (scopeId: string, type?: 'location' | 'group' | 'org'): string => {
    if (type === 'location') {
      const location = locationData[scopeId] ?? accessibleLocationMap[scopeId];
      return location?.name ?? '';
    }
    if (type === 'group') {
      const group = groupData[scopeId] ?? accessibleGroupMap[scopeId];
      return group?.name ?? '';
    }
    if (type === 'org') {
      const org = orgData[scopeId];
      return org?.name ?? '';
    }
    const scope = accessibleLocationMap[scopeId] ?? accessibleGroupMap[scopeId] ?? orgData[scopeId];
    return scope?.name ?? '';
  };

/**
 * Getter version of the app scope store - light wrapper around the Zustand store
 * This version is for backward compatibility and provides direct access to the store state (READ-ONLY)
 */
export const useAppScopeStore = () => {
  // Use shallow store to only subscribe to the properties we need
  const {
    locationData,
    groupData,
    orgData,
    selectedOrgId,
    selectedGroupId,
    forcedGroupId,
    selectedLocationIds,
    scopeHierarchy,
    accessibleLocationMap,
    accessibleGroupMap,
    accessibleScopeHierarchy,
    accessibleGroupIds,
    accessibleLocationIds,
    accessibleOrgIds,
    isSingleTypeScope,
  } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'selectedOrgId',
    'selectedGroupId',
    'forcedGroupId',
    'selectedLocationIds',
    'scopeHierarchy',
    'accessibleLocationMap',
    'accessibleGroupMap',
    'accessibleScopeHierarchy',
    'accessibleGroupIds',
    'accessibleLocationIds',
    'accessibleOrgIds',
    'isSingleTypeScope'
  );

  const getScopeName = useMemo(
    () => computeGetScopeName(locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap),
    [locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap]
  );

  const groupInclusiveSelectedLocationIds = useMemo(() => {
    const set = new Set([...selectedLocationIds]);

    if (accessibleGroupIds.includes(selectedGroupId)) {
      set.add(selectedGroupId);
    }

    return [...set];
  }, [selectedLocationIds, accessibleGroupIds, selectedGroupId]);

  // Return only state properties (no setters) - memoized for performance
  return useMemo(
    () => ({
      // Basic state
      locationData,
      groupData,
      orgData,
      selectedOrgId,
      selectedGroupId,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      scopeHierarchy,

      // Computed state
      accessibleLocationData: accessibleLocationMap,
      accessibleGroupData: accessibleGroupMap,
      accessibleScopeHierarchy,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,

      // Utility functions
      getScopeName,
      // Keep backward compatibility alias
      getLocationName: getScopeName,
    }),
    [
      // Basic state dependencies
      locationData,
      groupData,
      orgData,
      selectedOrgId,
      selectedGroupId,
      forcedGroupId,
      selectedLocationIds,
      groupInclusiveSelectedLocationIds,
      scopeHierarchy,
      // Computed state dependencies
      accessibleLocationMap,
      accessibleGroupMap,
      accessibleScopeHierarchy,
      accessibleGroupIds,
      accessibleLocationIds,
      accessibleOrgIds,
      isSingleTypeScope,
      // Utility function dependencies
      getScopeName,
    ]
  );
};

/**
 * Setter version of the app scope store - complex wrapper with calculations and validation logic
 * This version includes setters with calculations and validation logic
 */
export const useAppScopeStoreWithSetters = () => {
  const {
    setLocationData,
    setGroupData,
    setOrgData,
    setSelectedGroupId,
    setSelectedLocationIds,
    setSelectedOrgId,
    setForcedGroupId,
    appendLocationData,
    appendGroupData,
    appendOrgData,
    setScopeHierarchy,
    appendScopeHierarchy,
  } = useLocationGroupStore.getState();

  /**
   * We want these properties to be reactive.
   * We don't want to use the store directly because it will cause unnecessary re-renders.
   *
   * Components using this hook will rerender when the properties below are updated
   */
  const {
    locationData,
    groupData,
    orgData,
    selectedGroupId,
    selectedLocationIds,
    selectedOrgId,
    forcedGroupId,
    scopeHierarchy,
    accessibleLocationMap,
    accessibleGroupMap,
    accessibleScopeHierarchy,
    accessibleGroupIds,
    accessibleLocationIds,
    accessibleOrgIds,
    isSingleTypeScope,
  } = useScopeShallowStore(
    'locationData',
    'groupData',
    'orgData',
    'selectedGroupId',
    'selectedLocationIds',
    'selectedOrgId',
    'forcedGroupId',
    'scopeHierarchy',
    'accessibleLocationMap',
    'accessibleGroupMap',
    'accessibleScopeHierarchy',
    'accessibleGroupIds',
    'accessibleLocationIds',
    'accessibleOrgIds',
    'isSingleTypeScope'
  );

  const user = useRef(getUser());
  user.current = getUser();

  const _setSelectedOrgId = useCallback(
    (orgId: string) => {
      if (user.current?.userID) {
        setLoginData(user.current.userID, { latestOrganizationId: orgId });
      }
      setSelectedOrgId(orgId);
    },
    [setSelectedOrgId]
  );

  const _setSelectedGroupId = useCallback(
    (groupId: string, { skipValidation = false }: { skipValidation?: boolean } = {}) => {
      const isGroupAccessible = !!accessibleGroupMap[groupId];

      if (skipValidation || isGroupAccessible) {
        setSelectedGroupId(groupId);
        setForcedGroupId(groupId);
      } else {
        /**
         * If the group is not accessible, we still want to set the forcedGroupId.
         * There is potential for the group to not be accessible, but the locations within it are,
         * we still might need the groupId for the non-accessible group.
         */
        setSelectedGroupId('');
        setForcedGroupId(groupId);
      }
    },
    [accessibleGroupMap, setSelectedGroupId, setForcedGroupId]
  );

  const _setSelectedLocationIds = useCallback(
    (
      locationIds: string[],
      { skipValidation = false, onError }: { skipValidation?: boolean; onError?: (message: string) => void } = {}
    ) => {
      if (skipValidation) {
        setSelectedLocationIds(locationIds);
        if (user.current?.userID) {
          setLoginData(user.current.userID, { latestLocationIds: locationIds });
        }

        // This is a bad path to go down because we select a random location and set the groupId and orgId based on it
        // We're skipping validation now because DCA has multiple groups within the same org
        const referenceLocation = locationIds[0]
          ? // allow for parent location to be selected
            accessibleLocationMap[locationIds[0]] || accessibleGroupMap[locationIds[0]]
          : undefined;
        const groupId = referenceLocation?.groupId;
        const orgId = referenceLocation?.orgId;

        const actualGroupId = groupId ?? locationIds[0];

        if (actualGroupId) {
          _setSelectedGroupId(actualGroupId, { skipValidation: true });
          setForcedGroupId(actualGroupId);
        }
        if (orgId) {
          _setSelectedOrgId(orgId);
        }

        return;
      }

      // Location ids must be accessible
      if (locationIds.some((id) => !accessibleLocationMap[id])) {
        const msg = 'Setting an invalid or inaccessible location. AppScopeStore selections have been reset.';
        console.warn(msg);
        onError?.(msg);
        setSelectedLocationIds([]);
        _setSelectedGroupId('');
        setForcedGroupId('');
        _setSelectedOrgId('');
        return;
      }

      const locations = locationIds.map((id) => accessibleLocationMap[id]);
      const groupIds = new Set(locations.map((location) => location.groupId).filter(Boolean));

      if (groupIds.size > 1) {
        const msg = 'Locations must belong to the same group. AppScopeStore selections have been reset.';
        console.warn(msg);
        onError?.(msg);
        setSelectedLocationIds([]);
        _setSelectedGroupId('');
        setForcedGroupId('');
        _setSelectedOrgId('');
        return;
      }

      if (user.current?.userID) {
        setLoginData(user.current.userID, { latestLocationIds: locationIds });
      }

      /**
       * If we can determine the orgId and groupId from a selected location,
       * set them as well
       */
      const referenceLocation = locationIds[0] ? accessibleLocationMap[locationIds[0]] : undefined;
      const groupId = referenceLocation?.groupId;
      const orgId = referenceLocation?.orgId;

      if (groupId) {
        _setSelectedGroupId(groupId);
        setForcedGroupId(groupId);
      }

      if (orgId) {
        _setSelectedOrgId(orgId);
      }

      setSelectedLocationIds(locationIds);
    },
    [
      accessibleLocationMap,
      accessibleGroupMap,
      _setSelectedGroupId,
      _setSelectedOrgId,
      setSelectedLocationIds,
      setForcedGroupId,
    ]
  );

  const lastSentIdsToShellRef = useRef<string[]>([]);

  const getScopeName = useMemo(
    () => computeGetScopeName(locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap),
    [locationData, groupData, orgData, accessibleLocationMap, accessibleGroupMap]
  );

  // To memoize this state, everything within it has to be memoized
  const state = useMemo(() => {
    return {
      accessibleGroupIds: accessibleGroupIds,
      accessibleLocationIds: accessibleLocationIds,
      accessibleOrgIds: accessibleOrgIds,

      accessibleOrgData: orgData,
      /**
       * This is the source of truth for all location data.
       * It should only contain locations that the user has access to, never more than that.
       *
       * This completely depends on the input being passed to the store via `setScopeData`.
       */
      accessibleLocationData: accessibleLocationMap,
      accessibleGroupData: accessibleGroupMap,
      accessibleScopeHierarchy: accessibleScopeHierarchy,
      locationData,
      groupData,

      setLocationData: setLocationData,
      setGroupData: setGroupData,
      setOrgData: setOrgData,

      appendAccessibleLocationData: appendLocationData,
      appendAccessibleGroupData: appendGroupData,
      appendAccessibleOrgData: appendOrgData,

      selectedLocationIds,
      selectedGroupId,
      selectedOrgId,
      /**
       * All `selected-*` properties have to be accessible. `forcedGroupId` is different in that it may be inaccessible to the user.
       * This property reflects the groupId of selected locations, even if the group itself is not accessible to the user.
       * No further data about the group is available from the store.
       *
       * Both `selectedGroupId` and `forcedGroupId` and derived from the `selectedLocationIds`.
       * `selectedGroupId` may or may not be set (depending on access), but `forcedGroupId` will always be set.
       */
      forcedGroupId,

      setSelectedGroupId: _setSelectedGroupId,
      // Don't expose this function
      // setForcedGroupId,
      setSelectedOrgId: _setSelectedOrgId,
      setSelectedLocationIds: _setSelectedLocationIds,

      scopeHierarchy,
      setScopeHierarchy,
      appendScopeHierarchy,

      isSingleTypeScope: isSingleTypeScope,

      // Utility functions
      getScopeName,
      getLocationName: getScopeName,
    };
  }, [
    accessibleGroupIds,
    accessibleLocationIds,
    accessibleOrgIds,
    orgData,
    accessibleLocationMap,
    accessibleGroupMap,
    accessibleScopeHierarchy,
    locationData,
    groupData,
    setLocationData,
    setGroupData,
    setOrgData,
    appendLocationData,
    appendGroupData,
    appendOrgData,
    selectedLocationIds,
    selectedGroupId,
    selectedOrgId,
    forcedGroupId,
    _setSelectedGroupId,
    _setSelectedOrgId,
    _setSelectedLocationIds,
    scopeHierarchy,
    setScopeHierarchy,
    appendScopeHierarchy,
    isSingleTypeScope,
    getScopeName,
  ]);

  useEffect(() => {
    syncLocationsToShell({ selectedLocationIds, lastSentRef: lastSentIdsToShellRef });
  }, [selectedLocationIds]);

  return state;
};

/**
 * Granular selector — subscribes only to selectedLocationIds.
 * Prefer this over useAppScopeStore() when selectedLocationIds is the only needed value.
 */
export const useSelectedLocationIds = () => useScopeShallowStore('selectedLocationIds').selectedLocationIds;

/**
 * Granular selector — subscribes only to isSingleTypeScope.
 * Prefer this over useAppScopeStore() when isSingleTypeScope is the only needed value.
 */
export const useIsSingleTypeScope = () => useScopeShallowStore('isSingleTypeScope').isSingleTypeScope;
export function useOnScopeChange(
  callback: (newLocationIds: string[] | undefined, prevLocationIds: string[] | undefined) => void,
  cleanup?: () => void
) {
  // keep a ref to the latest callback so subscribe only needs to be set up once
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // subscribe to changes of selectedLocationIds
    const unsubscribe = useLocationGroupStore.subscribe(
      (state) => state.selectedLocationIds,
      (newLocationIds, prevLocationIds) => {
        callbackRef.current(newLocationIds, prevLocationIds);
      }
    );
    return () => {
      // unsubscribe from changes of selectedLocationIds
      unsubscribe();
      if (cleanup) {
        cleanup();
      }
    };
  }, [cleanup]);
}
